<script setup>
/* eslint semi: 1 */
import _ from 'lodash';
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { useStore } from 'vuex';
import { selectionStore } from '@/pinia/stores/selectionStore';
import { COMPONENT_KEY } from '@/pinia/stores/storeUtils';
import { useMediaQuery } from '@/composables/media/use-media-query';
import { useMediaUpdate } from '@/composables/media/use-media-update';
import { useLocalStorageStore } from '@base-shared/stores/local-storage';
import { useFileUploadStorage } from '@/composables/file-upload/use-file-upload-storage';
import { useFileUploadApi } from '@/composables/file-upload/use-file-upload-api';
import { useFileUploadQueue } from '@/composables/file-upload/use-file-upload-queue';
import { useFileUploaderStore } from '@/pinia/stores/file-upload/fileUploaderStore';
import { useDropzonePositioning } from '@/composables/file-upload/use-dropzone-positioning';
import { useFileUploader, TARGET_COMPONENTS } from '@/composables/file-upload/use-file-uploader';
import { useFileUploaderPolling } from '@/composables/file-upload/use-file-uploader-polling';
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';
import CONST from '@/globals/constants';
import EventBus from '@/components/mixins/event-bus-mixin';
import { storeToRefs } from 'pinia';

// Use component instance to call methods from the AuthMixin
const { t } = useI18n();
const $utils = inject('$utils');
const $dataStore = inject('$dataStore');
const $route = useRoute();

// Pinia stores
const localStorageStore = useLocalStorageStore();
const fileUploaderStore = useFileUploaderStore();
const cognitoAuthStore = useCognitoAuthStore();
// Legacy stores
const filesSelectionStore = selectionStore(COMPONENT_KEY.FILES_SCREEN);
const ledSelectionStore = selectionStore(COMPONENT_KEY.LED_SCREEN);
const videowallSelectionStore = selectionStore(COMPONENT_KEY.VIDEOWALL_SCREEN);
const vuexStore = useStore();

// Composables
const fileUploader = useFileUploader();
const storage = useFileUploadStorage();
const api = useFileUploadApi();
const queue = useFileUploadQueue();
const { startPolling, stopPolling } = useFileUploaderPolling(handleUploadStatusResponse);
const isMediaV2Enabled = computed(() => localStorageStore.isAryaMediaV2Enabled);
const dropzonePos = useDropzonePositioning(fileUploader, isMediaV2Enabled);
// Exposes media v2 composables
const mediaQuery = useMediaQuery();
const mediaUpdate = useMediaUpdate();

// Selection data for different pages
const { resourceTags: filesResourceTags } = storeToRefs(filesSelectionStore());
const { resourceTags: ledResourceTags, ledTags: ledResolutionTags } = storeToRefs(ledSelectionStore());
const { resourceTags: vwResourceTags, videowallTags: vwResolutionTags } = storeToRefs(videowallSelectionStore());
// Vuex states
const vuexSchedulePageSelectedPlayerFullDetailsState = computed(() => vuexStore.state.schedulePageSelectedPlayerComponent.scheduleItemFullDetails);
const vuexTagsSliderComponentSelectedTags = computed(() => vuexStore.state.tagSliderComp.selectedTagsFromLocalStorage);

// Refs
const uploadedFiles = ref([]);
const selectedTagsFromSlider = ref([]);
const cleanupVisibilityListenerCallback = ref(null);
// NOTE: Reference reactive refs from useFileUploader
const dropzoneElementRefs = fileUploader.dropzoneElementRefs;

watch($route, () => {
  // NOTE: useFileUploader watches the route internally
  // We still need to reset the selected tags from the slider
  selectedTagsFromSlider.value = [];
});

watch(() => fileUploaderStore.deletedFile, () => {
  // If a file is deleted from the Pinia store, update the
  // local storage to reflect this change
  storage.getFiles();
}, { deep: true });

watch(isMediaV2Enabled, () => {
  setDefaultData();
}, { immediate: true });

watch(vuexSchedulePageSelectedPlayerFullDetailsState, (newVal, prevVal) => {
  if (_.isEqual(newVal, prevVal)) return;

  const createScheduleSummary = (schedule) => ({
    scheduleId: schedule?.scheduleId ?? null,
    sideBar: schedule?.sideBar ?? {},
    events: (schedule?.events ?? []).map(({ _edit, ...event }) => event),
    addsEvents: (schedule?.addsEvents ?? []).map(({ _edit, ...event }) => event)
  });
  const newScheduleSummary = createScheduleSummary(newVal);
  const prevScheduleSummary = createScheduleSummary(prevVal);
  if (_.isEqual(newScheduleSummary, prevScheduleSummary)) return;

  const filesInLocalStorage = storage.getFiles();
  for (const fileItem of filesInLocalStorage) {
    const targetScheduleId = fileItem._edit && fileItem._edit.targetPlayerId;
    const isForSchedulePageWithSelectedPlayer = (fileItem._edit && fileItem._edit.targetComponent === TARGET_COMPONENTS.SCHEDULE_PLAYER_SELECTED);

    if ((fileItem._edit && fileItem._edit.addToSchedule) && (isForSchedulePageWithSelectedPlayer && targetScheduleId === newVal.scheduleId)) {
      fileItem._edit.addToSchedule.selectedPlayerScheduleDetails = newScheduleSummary;
    }
  }

  storage.setFiles(filesInLocalStorage);
}, { deep: true });

watch(vuexTagsSliderComponentSelectedTags, (newVal) => {
  selectedTagsFromSlider.value = newVal;
}, { deep: true });

/**
 * Advances a single file through the post-conversion
 * pipeline after its S3 upload completes or the
 * conversion-finished signal arrives via `handleSVGMessage`.
 */
function updateFileDataInLocalStorage (fileItemSrc) {
  queue.advanceFile(fileItemSrc, {
    scheduleDetails: vuexSchedulePageSelectedPlayerFullDetailsState.value,
    orientation: $dataStore.getMediaOrientation(),
    onStateChanged: (action, files, updatedFile) => {
      fileUploader.storeUploadEvent(action, { files, updatedFile });
    },
    onDelete: (file) => {
      fileUploader.storeUploadEvent('deletedFile', { files: storage.getFiles(), deletedFile: file });
    }
  });
}

/**
 * Step 5: Handle the S3 POST request result for a file.
 * @param status The status of the S3 upload ('success' or 'error').
 * @param message The message returned from the S3 upload.
 * @param resourceId The resource ID of the uploaded file.
 */
function actionsAfterS3PostRequest (status, message, resourceId) {
  // Map the uploaded files to update their S3 upload status and message
  uploadedFiles.value = (uploadedFiles.value ?? []).map(item => {
    item._edit.uploadRequests.submitToS3End = DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss'); // moment().format('YYYY-MM-DD HH:mm:ss')

    if (item._edit.resourceId === resourceId) {
      item._edit.uploadRequests.submitToS3Status = status;
      item._edit.uploadRequests.submitToS3Message = message;

      // Update File in localStorage
      const targetFile = storage.getFiles().find((file) => file.uid === item.uid);
      if (targetFile) {
        targetFile._edit.uploadRequests.submitToS3Status = status;
        targetFile._edit.uploadRequests.submitToS3Message = message;
        updateFileDataInLocalStorage(targetFile);
      }
    }

    // Inform backend that S3 upload is failed. Fire-and-forget
    if (item._edit.uploadRequests.submitToS3Status === 'error') {
      api.notifyUploadFailed(resourceId, item._edit.uploadRequests.submitToS3Message);
    }

    return item;
  });

  const isAllRequestsDone = uploadedFiles.value.every((item) => ['success', 'error'].indexOf(item._edit.uploadRequests.submitToS3Status) >= 0);
  if (!isAllRequestsDone) return;

  // Clear the uploaded files and dropzone after all S3 upload requests are done
  uploadedFiles.value = [];
  fileUploader.clearFilesInDropzone();
}

/**
 * Step 4: Handle the S3 upload for a file.
 * Hook function in :http-request prop in dropzone to submit
 * uploaded files into S3 which is triggered from
 * {@link fileUploader.submitFilesInDropzone}
 * @param fileFromDropzone - The file object from the dropzone.
 */
function handleUploadFileToS3 (fileFromDropzone) {
  const uploadedItem = uploadedFiles.value.find((item) => item.uid === fileFromDropzone.file.uid);
  if (!uploadedItem?._edit?.post?.url || !uploadedItem?._edit?.post?.fields) {
    return;
  }

  // Update the S3 upload status and message for the uploaded item
  uploadedItem._edit.uploadRequests.submitToS3Status = 'uploading';
  uploadedItem._edit.uploadRequests.submitToS3Message = 'Uploading';
  uploadedItem._edit.uploadRequests.submitToS3Start = DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss'); // moment().format('YYYY-MM-DD HH:mm:ss')
  updateFileDataInLocalStorage(uploadedItem);

  // Perform the S3 upload using the provided URL and fields.
  const fields = uploadedItem._edit.post.fields;
  const url = uploadedItem._edit.post.url;
  const generatedFilename = uploadedItem._edit.generatedFilename;
  api.uploadToS3(url, fields, fileFromDropzone.file, generatedFilename)
    .then(({ status, message, resourceId }) => {
      actionsAfterS3PostRequest(status, message, resourceId);
    });
}

/**
 * Step 3.2: Process new files with media instance
 * This function is called after all create media instance
 * requests have been processed. It checks for new files
 * that need to be uploaded to S3, updates the local storage,
 * and submits the files to S3.
 */
function processNewFilesWithInstance () {
  $utils.mainLoader(false);
  dropzonePos.adjust('inActive'); // Hide Dropzone

  const newFilesForS3Upload = uploadedFiles.value.filter((file) => file._edit && !file._edit.existingFile);
  const existingFiles = uploadedFiles.value.filter((file) => file._edit && file._edit.existingFile);
  fileUploader.storeUploadEvent('processNewFilesWithInstance', { files: uploadedFiles.value });

  // Added a timeout to have time to receive the 'processNewFilesWithInstance' event from other components
  window.setTimeout(() => {
    $utils.mainLoader(false);
    // Uploaded files are empty
    const areAllFilesExisting = !newFilesForS3Upload.length && existingFiles.length;
    if (!uploadedFiles.value.length || areAllFilesExisting) {
      if (areAllFilesExisting) {
        // All files are existing files
        existingFiles.forEach((file) => updateFileDataInLocalStorage(file));
      }
      fileUploader.clearFilesInDropzone();
      uploadedFiles.value = [];
      return;
    }

    // Submit files to S3 upload
    fileUploader.submitFilesInDropzone();
    uploadedFiles.value.forEach((file) => updateFileDataInLocalStorage(file));
  }, 10);
}

function getDuplicateMediaErrorFiles (files = uploadedFiles.value) {
  return files.filter((file) => file?._edit?.uploadRequests?.createMediaInstanceStatusErrorType === 'DuplicateResourceNameError');
}

function buildDuplicateMediaPreviewItems (duplicateMediaErrorFiles) {
  const duplicateMediaFiles = [];
  duplicateMediaErrorFiles.forEach((file) => {
    const media = file?._edit?.uploadRequests?.createMediaInstanceResponseData?.resource;
    if (!media) return;
    // TODO: Consider removing this because it doesn't seem to be used anywhere.
    if (!media._edit) media._edit = {};
    media._edit.uid = `duplicateFile_${media.resourceId}`;
    media._edit.iFrameStatus = 'default';
    duplicateMediaFiles.push(media);
  });

  return duplicateMediaFiles;
}

function showDuplicateErrorsNotification (duplicateMediaErrorFiles) {
  if (!duplicateMediaErrorFiles.length) return;

  const names = duplicateMediaErrorFiles
    .map((file) => file?._edit?.uploadRequests?.createMediaInstanceResponseData?.resource?.name)
    .filter(Boolean);
  const duplicateMediaFiles = buildDuplicateMediaPreviewItems(duplicateMediaErrorFiles);

  const isMultipleDuplicateFiles = duplicateMediaErrorFiles.length > 1;
  EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
    type: 'error',
    title: isMultipleDuplicateFiles
      ? t('fileUpload.multipleDuplicateFileUploadErrorTitle')
      : t('fileUpload.duplicateFileUploadErrorTitle'),
    description: isMultipleDuplicateFiles
      ? t('fileUpload.multipleDuplicateFileUploadErrorDescription', { mediaNames: names.join(', ') })
      : t('fileUpload.duplicateFileUploadErrorDescription', { mediaName: names[0] }),
    alert: {
      message: t('fileUpload.duplicateFileUploadErrorMessage', {
        pleaseNote: t('fileUpload.pleaseNote')
      })
    },
    alertMessageI18n: {
      key: 'fileUpload.duplicateFileUploadErrorMessage',
      params: {
        pleaseNote: {
          type: 'strong',
          text: t('fileUpload.pleaseNote')
        }
      }
    },
    preview: {
      title: t('fileUpload.duplicateFileUploadPreviewTitle'),
      items: duplicateMediaFiles,
      mediaOrientation: $dataStore.getMediaOrientation()
    },
    actions: [
      {
        text: isMultipleDuplicateFiles ? t('buttons.replaceExistingFiles') : t('buttons.replaceExistingFile'),
        color: 'alternative',
        onClick: async () => {
          await handleFileUploadPopUpActions({ replaceDuplicateFiles: true });
        }
      },
      {
        text: t('buttons.keepBoth'),
        color: 'alternative',
        onClick: async () => {
          await handleFileUploadPopUpActions({ copyDuplicateFiles: true });
        }
      }
    ],
    callbacks: {
      eventOnClose: async () => {
        await handleFileUploadPopUpActions({ closeDuplicateFiles: true });
      }
    }
  });
}

/**
 * Step 3.1: Handle the result of the create media instance request
 * This function is called after the backend responds to the
 * create media instance request. It updates the uploaded
 * files state, handles errors, and determines whether to proceed
 * with S3 upload or show error pop-ups.
 */
async function actionAfterCreatingMediaInstanceRequest (status, responseData, requestData) {
  uploadedFiles.value = queue.applyCreateInstanceResult(uploadedFiles.value, { status, responseData, requestData });

  // If all create-instance requests are done, process errors or continue to S3
  const isAllMediaInstanceRequestDone = uploadedFiles.value.every(
    (mediaItem) => ['requesting', 'retry'].indexOf(mediaItem._edit.uploadRequests.createMediaInstanceStatus) < 0
  );
  if (!isAllMediaInstanceRequestDone) return;

  // Only "copy" is allowed for duplicates the user has no access to, so resolve
  // them silently first; the notification's Replace action must never target these.
  const noAccessDuplicateFiles = uploadedFiles.value.filter((file) => file?._edit?.uploadRequests?.createMediaInstanceStatusErrorType === 'DuplicateResourceNameNoAccessError');
  if (noAccessDuplicateFiles.length) {
    await autoResolveNoAccessDuplicates(noAccessDuplicateFiles);
  }

  const duplicateMediaErrorFiles = getDuplicateMediaErrorFiles();
  const normalErrorFiles = uploadedFiles.value.filter((file) => file?._edit?.uploadRequests?.createMediaInstanceStatusErrorType === 'normalError');

  if (duplicateMediaErrorFiles.length || normalErrorFiles.length) {
    dropzonePos.hide(); // Hide dropzone to not conflict with pop up notification
    $utils.mainLoader(false); // Hide loading animation

    if (normalErrorFiles.length) {
      const normalErrors = fileUploader.translateNormalErrorDescription(normalErrorFiles);
      const message = normalErrors.map((file) => file._edit.uploadRequests.createMediaInstanceResponseData.description);
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'error',
        alert: {
          message
        },
        callbacks: {
          eventOnClose: () => {
            handleFileUploadPopUpActions({ closeNormalErrors: true });
          }
        }
      });
    } else if (duplicateMediaErrorFiles.length) {
      showDuplicateErrorsNotification(duplicateMediaErrorFiles);
    }
  } else {
    processNewFilesWithInstance();
  }
}

/**
 * Step 3: Create Media Instance Request
 * Construct the request data for creating a media instance
 * for the given file including metadata, tags (selected from
 * each page) and send it to the backend using
 * {@link api.createMediaInstance}.
 * @param file - The file object for which to create a media instance.
 */
function createMediaInstanceBackendRequest (file) {
  const fileExt = file.name.split('.').pop().toLowerCase();
  const name = $utils.filterMediaName(file.name.split('.').slice(0, -1).join('.'));
  const requestData = {
    name: `${name}.${fileExt}`,
    fileExtension: fileExt,
    mimeType: file.type,
    fileSize: file.size,
    uid: file.uid,
    hash: file.hashMD5,
    alert: !!fileUploader.usedForAlertPage.value
  };

  if ($route.name === CONST.ROUTE_NAME_MEDIA && filesResourceTags.value.length) {
    requestData.tags = filesResourceTags.value.map((t) => t.value);
  } else if ($route.name === CONST.ROUTE_NAME_LED && ledResourceTags.value.length) {
    requestData.tags = ledResourceTags.value.map((t) => t.value);
  } else if ($route.name === CONST.ROUTE_NAME_VIDEOWALL && vwResourceTags.value.length) {
    requestData.tags = vwResourceTags.value.map((t) => t.value);
  } else {
    requestData.tags = selectedTagsFromSlider.value ?? [];
  }

  // Add led property for led page
  if ($route.name === CONST.ROUTE_NAME_LED && !_.isEmpty(ledResolutionTags.value)) {
    const parseLedTag = $utils.parseLedTag(ledResolutionTags.value[0]);
    requestData.led = parseLedTag.dimension;
  }

  // Add led property for videowall page
  if ($route.name === CONST.ROUTE_NAME_VIDEOWALL && !_.isEmpty(vwResolutionTags.value)) {
    const parseVideoWallTag = $utils.parseLedTag(vwResolutionTags.value[0]);
    requestData.led = parseVideoWallTag.dimension;
  }

  // TODO(media-v2-cleanup): Add tags property for media/led/videowall v2 pages
  if (isMediaV2Enabled.value) {
    // Add tags property from mediaTagsInV2
    // NOTE: no tags for alert
    if ($route.name !== CONST.ROUTE_NAME_ALERT && mediaQuery.mediaTags.value?.length) {
      requestData.tags = mediaQuery.mediaTags.value;
    }

    // Add led property for led/videowall pages
    if ([CONST.ROUTE_NAME_LED, CONST.ROUTE_NAME_VIDEOWALL].includes($route.name) && mediaQuery.selectedLedTag.value?.value) {
      requestData.led = mediaQuery.selectedLedTag.value.dimension;
    }
  }

  // Send request to backend to create media instance on
  // the background.
  api.createMediaInstance(requestData, $dataStore.getMediaOrientation())
    .then(({ status, responseData }) => {
      actionAfterCreatingMediaInstanceRequest(status, responseData, requestData);
    });
}

/**
 * Step 2: Hashed file processing
 * Process a file once its hash has been computed that will
 * then start the create media instance request for each file
 * to the backend using {@link api.createMediaInstance}
 * on the background from which the result will be handled
 * by {@link queue.applyCreateInstanceResult} to
 * update the file's state and queue accordingly.
 *
 * @param hashVal - The computed hash value of the file.
 * @param newFile - The file object that has been hashed.
 */
async function processHashedFileQueue (hashVal, newFile) {
  // Mutate the local array to add the hash value to the file object
  const uploadedFile = uploadedFiles.value.find((item) => item.uid === newFile.uid);
  if (uploadedFile) uploadedFile.hashMD5 = hashVal;

  // If all queued files have hashes, start the API upload process
  const filesWithSha256 = uploadedFiles.value.filter((f) => !!f.hashMD5);
  if (filesWithSha256.length !== uploadedFiles.value.length) return;

  // Once all the files have their hash computed, proceed
  // to create media instances for each file except those
  // that already have the decorated _edit property, which
  // indicates they have been processed before.
  uploadedFiles.value = uploadedFiles.value.map((file) => {
    if (file._edit) { return file; }
    // Initialize the _edit property for the file
    file._edit = fileUploader.getUploadFileDefaultEditData();
    // Start the create media instance request for the file
    createMediaInstanceBackendRequest(file);
    return file;
  });
}

/**
 * Step 1: Handle the file input change event from the dropzone.
 * Hook function in :on-change prop in dropzone to handle
 * changes to the dropzone file input event per each file.
 * @param file The file that was added or changed.
 * @param lists The list of files currently in the dropzone.
 */
async function handleDropzoneFileChange (file, lists) {
  fileUploader.syncDropzoneFromDom();

  // Get file type and validate it against the dropzone's accepted types
  const uploadedFileType = fileUploader.getTypeFromElementPlus(file);
  if (!fileUploader.isFileTypeValid(uploadedFileType)) {
    dropzonePos.adjust('inActive'); // Hide Dropzone
    fileUploader.clearInput();
    EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
      type: 'error',
      alert: {
        message: t('label.formatNotSupported', { format: uploadedFileType })
      }
    });

    return;
  }

  // Check if already in process list
  const isFileProcessed = !!uploadedFiles.value.find((item) => item.uid === file.uid && !item._edit);
  if (isFileProcessed) {
    fileUploader.clearInput();
    return;
  }

  // If not in the process file list then add this on the list
  const newFile = $utils.clone(file);
  uploadedFiles.value.push(newFile);
  $utils.mainLoader(true);
  fileUploader.clearInput();

  // If the file already has a hashMD5, we can skip the computation.
  if (newFile.hashMD5) return;

  try {
    // Compute Hash
    const hashVal = await queue.hashFile(file.raw);
    processHashedFileQueue(hashVal, newFile);
  } catch (error) {
    EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
      type: 'error',
      alert: {
        message: t('fileUpload.hashMD5ComputationError')
      }
    });
  }
}

function removeFilesWithNormalErrors () {
  // Hide the normal errors  pop-up and remove files with normal errors
  uploadedFiles.value = uploadedFiles.value.filter(
    (file) => file?._edit?.uploadRequests?.createMediaInstanceStatusErrorType !== 'normalError'
  );

  // If there are still files with duplicate errors, show the duplicate file errors pop-up
  const duplicateMediaErrorFiles = getDuplicateMediaErrorFiles();
  if (duplicateMediaErrorFiles.length) {
    showDuplicateErrorsNotification(duplicateMediaErrorFiles);
    return;
  }

  processNewFilesWithInstance(); // Process All files with instance
}

function removeFilesWithDuplicateErrors () {
  // Remove files with duplicate errors
  uploadedFiles.value = uploadedFiles.value.filter(
    (file) => file._edit.uploadRequests.createMediaInstanceStatusErrorType !== 'DuplicateResourceNameError'
  );

  processNewFilesWithInstance(); // Hide PopUp
}
/**
 * Send a batch of duplicate-name files to the backend for resolution
 * and apply the per-file result (status, response data) in place.
 * @param duplicateFiles - The files to resolve.
 * @param mode - The mode of resolution, either 'copy' or 'replace'.
 */
async function resolveDuplicatesWithMode (duplicateFiles, mode) {
  const results = await fileUploader.resolveDuplicateFiles(
    duplicateFiles, mode, !!fileUploader.usedForAlertPage.value
  );

  results.forEach(({ status, responseData, requestData }) => {
    uploadedFiles.value.forEach((fileItem) => {
      if (fileItem.uid === requestData.uid) {
        fileItem._edit.uploadRequests.createMediaInstanceStatus = status;
        fileItem._edit.uploadRequests.createMediaInstanceResponseData = responseData;

        if (status === 'success') {
          fileItem._edit.resourceId = responseData.resourceId;
          fileItem._edit.post = responseData.post;
          fileItem._edit.generatedFilename = responseData.filename;
          fileItem._edit.uploadRequests.createMediaInstanceStatusErrorType = null;
        }
      }
    });
  });

  // Collect error messages across all results, then show error message.
  const uploadErrorMessages = uploadedFiles.value
    .filter((fileItem) => fileItem._edit.uploadRequests.createMediaInstanceStatus === 'error')
    .map((fileItem) => fileItem._edit.uploadRequests.createMediaInstanceResponseData.message);

  if (uploadErrorMessages.length) {
    EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
      type: 'error',
      alert: {
        message: uploadErrorMessages
      }
    });
  }
}

/**
 * Silently resolve duplicates the user has no access to as "copy";
 * "replace" is rejected by the backend for this error type.
 * @param noAccessFiles - The files with a DuplicateResourceNameNoAccessError.
 */
async function autoResolveNoAccessDuplicates (noAccessFiles) {
  await resolveDuplicatesWithMode(noAccessFiles, 'copy');

  // Drop any that failed to resolve so they don't linger with a stale error state
  uploadedFiles.value = uploadedFiles.value.filter(
    (file) => !noAccessFiles.includes(file) || file._edit.uploadRequests.createMediaInstanceStatus === 'success'
  );
}

/**
 * Handle the resolution of duplicate files by either copying
 * or replacing them. Only reachable for accessible duplicates,
 * no-access ones are already resolved by {@link autoResolveNoAccessDuplicates}.
 * @param mode - The mode of resolution, either 'copy' or 'replace'.
 */
async function copyOrForceFileUpload (mode) {
  const duplicateFiles = getDuplicateMediaErrorFiles(uploadedFiles.value);

  // Inform backend for copy or replace files sequentially, then act on all results at once
  $utils.mainLoader(true);

  await resolveDuplicatesWithMode(duplicateFiles, mode);

  // If there are successful uploads, process them further; otherwise, hide the main loader.
  uploadedFiles.value = uploadedFiles.value.filter((file) => file._edit.uploadRequests.createMediaInstanceStatus === 'success');
  if (uploadedFiles.value.length > 0) {
    processNewFilesWithInstance();
  } else {
    $utils.mainLoader(false);
  }
}

/**
 * Handle actions from the file upload pop-up, such as closing
 * error messages or resolving duplicate files.
 */
async function handleFileUploadPopUpActions (event) {
  if (!event) return;
  if (event.closeNormalErrors) {
    removeFilesWithNormalErrors();
  } else if (event.closeDuplicateFiles) {
    removeFilesWithDuplicateErrors();
  } else if (event.replaceDuplicateFiles || event.copyDuplicateFiles) {
    await copyOrForceFileUpload(event.copyDuplicateFiles ? 'copy' : 'replace');
  }
}

/**
 * Step 6: Handle the upload status response from the
 * backend. This function updates the file's conversion
 * status in local storage and syncs with the media list if
 * necessary.
 * @param data - The data object containing the upload status response from the backend.
 */
function handleUploadStatusResponse (data) {
  const fileItem = storage.getFiles().find((file) => file._edit?.resourceId === data?.resourceId);
  if (!_.isPlainObject(data) || !fileItem) return;

  if (data?.media && data?.status === CONST.FILE_UPLOAD_ITEM_STATUS.FINISHED) {
    // Update the file's conversion status in local storage when the conversion is finished
    if (fileItem) {
      // NOTE: Propagate the conversion finished status to the file's
      // local storage data to update the UI accordingly.
      fileItem.percentage = 100; // Set percentage to 100 when conversion is finished
      fileItem.status = data.status; // Update the status to 'finished'
      fileItem._edit.uploadRequests.conversionFinished = true;
      fileItem._edit.uploadRequests.conversionFinishedSentBySVG = true;
      fileItem._edit.uploadRequests.createMediaInstanceResponseData = data.media;

      updateFileDataInLocalStorage(fileItem);
      // Store the 'finishedFile' event in the Pinia store
      // to notify legacy Files page about the finished status.
      fileUploader.storeUploadEvent('finishedFile', {
        files: storage.getFiles(),
        updatedFile: fileItem
      });
    }

    // Sync updated file data with media list if user is
    // in media v2 page to update the media type and
    // thumbnail after conversion is done in backend
    if ((fileUploader.isMediaPage.value && isMediaV2Enabled.value) && !!data.media?.resourceId) {
      mediaUpdate.syncUpdated(data.media);
    }
  } else if ([
    CONST.FILE_UPLOAD_ITEM_STATUS.UPLOAD,
    CONST.FILE_UPLOAD_ITEM_STATUS.QUEUED,
    CONST.FILE_UPLOAD_ITEM_STATUS.PROCESS,
    CONST.FILE_UPLOAD_ITEM_STATUS.PROGRESS,
    CONST.FILE_UPLOAD_ITEM_STATUS.FAILED,
    CONST.FILE_UPLOAD_ITEM_STATUS.CANCEL
  ].includes(data.status)) {
    // NOTE: Added other statuses to handle and update the
    // file's progress and current status in local storage
    // which will be used to present in the UI current
    // information about the file's upload/conversion progress.
    fileItem.percentage = data.progress ?? fileItem.percentage;
    fileItem.status = data.status;
    updateFileDataInLocalStorage(fileItem);
  }
}

/**
 * Handle messages from the SVG iframe, particularly for
 * file conversion status updates and progress tracking
 * to the local storage.
 */
const handleSVGMessage = (event) => {
  // Make sure event.origin is from the backend to prevent accepting message out of nowhere
  const sessionStage = cognitoAuthStore.sessionStage ?? CONFIG.GLOBAL_STAGE;
  const apiOrigin = (new URL($utils.getStageAPIUrl(sessionStage))).origin;
  if (event.origin !== apiOrigin || !_.isPlainObject(event.data)) return;

  handleUploadStatusResponse(event.data);
};

const handleBrowserReload = (event) => {
  event = event || window.event;
  const hasRunningUploads = storage.getFiles().find((file) => {
    return ['uploading', 'notYetStarted'].includes(file?._edit?.uploadRequests?.submitToS3Status);
  });

  if (hasRunningUploads) {
    const message = 'Uploading Media Files';
    event.preventDefault(); // Cancel the event as stated by the standard.
    event.returnValue = message; // Chrome requires returnValue to be set.
  }
};

function addListeners () {
  window.addEventListener('message', handleSVGMessage);
  window.addEventListener('beforeunload', handleBrowserReload);
}
function removeListeners () {
  window.removeEventListener('message', handleSVGMessage);
  window.removeEventListener('beforeunload', handleBrowserReload);
}

function listenGlobalEvents () {
  // NOTE: SORTING_START / SORTING_END are handled by useDropzonePositioning
  // Clear all files in local storage when user signs out
  EventBus.$on('SIGNED_OUT', () => storage.clearAll());
}

/**
 * Set default data for the file uploader component,
 * including creating a layout guide and storing the current
 * files in local storage to the Pinia store.
 *
 * See {@link useFileUploader} and {@link useDropzonePositioning}
 * for more details on how the dropzone config and positioning
 * are applied reactively.
 */
function setDefaultData () {
  if (!$('#fileUploaderLayoutGuide').length) {
    $('<div id="fileUploaderLayoutGuide" class="guide"></div>').insertAfter($('#fileUploader'));
  }

  fileUploader.storeUploadEvent('setDefaultData', { files: storage.getFiles() });
  // Note: useFileUploader applies the route config
  // reactively on setup (immediate: true)
  dropzonePos.hide();
  dropzonePos.adjust('watchDragFiles');
}

onBeforeUnmount(() => {
  removeListeners();
  // Cleanup the page/tab visibility listener
  cleanupVisibilityListenerCallback.value?.();
  // Stop polling file uploads from storage on unmount
  stopPolling();
});

onMounted(() => {
  addListeners();
  setDefaultData();
  listenGlobalEvents();
  storage.cleanupOnMount();

  // Deactivate the dropzone when the page/tab becomes hidden.
  cleanupVisibilityListenerCallback.value = $utils.handleDocumentVisibilityChange(
    () => dropzonePos.adjust('inActive'),
    () => {}
  );
  // Start polling file uploads from storage on mount
  startPolling();
});
</script>

<template>
  <div
    id="fileUploader"
    :class="['file-uploader media-file-upload not-active', {
    'set-dropzone-in-media-list-modal': fileUploader.config.setDropzoneInMediaListModal,
    }]">
    <template
      v-for="(uploader, indx) in fileUploader.dropzones.value"
      :key="`uploader${indx}`"
    >
      <el-upload
        v-bind="{ class: ['file-uploader-item', { 'disabled-uploader': uploader.disabled }] }"
        :id="uploader.id"
        :ref="(el) => { dropzoneElementRefs[uploader.id] = el }"
        :action="''"
        :auto-upload="false"
        :drag="true"
        :multiple="uploader.multipleUpload"
        :show-file-list="false"
        :on-change="handleDropzoneFileChange"
        :http-request="handleUploadFileToS3"
        :disabled="uploader.disabled">

        <el-icon v-if="uploader.showUploadIcon" class="icon"><Upload /></el-icon>
        <div v-if="uploader.showUploadButtonAndText" class="el-upload__text">
          <div class="upload-icon"></div>
        </div>
      </el-upload>
    </template>

    <div class="all-uploaded-files-wrapper">
      <template v-for="(file, indx) in storage.files.value" :key="indx">
        <div class="file-item">
          <template v-if="(file._edit && file._edit.uploadRequests) && file._edit.uploadRequests.createMediaInstanceResponseData">
            <div v-if="file._edit.uploadRequests.createMediaInstanceResponseData.preview" class="upload-preview">
              <template v-if="file._edit.uploadRequests.createMediaInstanceResponseData.preview.type === 'svg'">
                <!-- Project Preview -->
                <template v-if="file._edit.uploadRequests.createMediaInstanceResponseData.preview.type && file._edit.uploadRequests.createMediaInstanceResponseData.preview.type === 'svg'">
                  <iframe v-if="!fileUploader.usedForEditProjectTemplatePage.value"
                    :id="`fileUploading_${file._edit.resourceId}`"
                    :src="file._edit.uploadRequests.createMediaInstanceResponseData.preview.url"
                    :data-src="$utils.trimDataSrc(file._edit.uploadRequests.createMediaInstanceResponseData.preview.url)"
                    :class="['object-fit_cover_center', 'template-target-on-refresh-token', 'media-project-file', `media-preview-${file._edit.uploadRequests.createMediaInstanceResponseData.uid}`]">
                  </iframe>
                </template>
              </template>
              <template v-else>
                <img v-if="file._edit.uploadRequests.createMediaInstanceResponseData.preview.type === 'img'" :src="file._edit.uploadRequests.createMediaInstanceResponseData.preview.url" class="object-fit_cover_center"/>
                <div v-if="(file._edit.uploadRequests.createMediaInstanceResponseData && file._edit.uploadRequests.createMediaInstanceResponseData.type) && file._edit.uploadRequests.createMediaInstanceResponseData.type === 'audio'" class="audio"></div>
              </template>
            </div>

            <div class="details">
              <div class="name"><div class="name-text" v-if="file.name" :title="file.name">{{file.name}}</div></div>
                <div class="status">{{`Create Instance: ${((file._edit && file._edit.uploadRequests.createMediaInstanceStatus) && file._edit.uploadRequests.createMediaInstanceStatus === 'success') ? 'Success' : 'Pending'}`}}</div>
                <div class="status">{{`S3 Upload: ${(file._edit && (file._edit.uploadRequests && file._edit.uploadRequests.submitToS3Status === 'success')) ? 'Success' : 'Pending'}`}}</div>
                <div class="status">{{`Conversion: ${((file._edit && file._edit.uploadRequests) && file._edit.uploadRequests.conversionFinished) ? 'Success' : 'Pending'}`}}</div>
                <div v-if="file._edit && file._edit.targetComponent === 'schedulePagePlayerSelected'" class="status">{{`Save Schedule: ${((file._edit && file._edit.addToSchedule) && (file._edit.addToSchedule.addToScheduleRequestStatus === 'success')) ? 'Success' : 'Pending'}`}}</div>
                <div class="status">{{`Target Component: ${(file._edit && file._edit.targetComponent) ? file._edit.targetComponent : 'None'}`}}</div>
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" src="./file-uploader.scss"></style>
